import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import store from "@/store";
import { httpStatus, parseError } from "./api-service-helper";

const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL =
      process.env.NODE_ENV !== "development"
        ? window.location.origin + /api/
        : process.env.VUE_APP_APIURL;

    Vue.axios.interceptors.request.use(
      config => {
        store.commit("loadingStore/startLoading");
        return config;
      },
      error => {
        store.commit("loadingStore/finishLoading");
        return Promise.reject(error);
      }
    );

    Vue.axios.interceptors.response.use(
      response => {
        store.commit("loadingStore/finishLoading");
        return response;
      },
      error => {
        store.commit("loadingStore/finishLoading");
        return Promise.reject(error);
      }
    );
  },

  setHeader(token) {
    Vue.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  },

  query(resource, params) {
    return Vue.axios.get(resource, params).catch(parseError);
  },

  get(resource, id) {
    return Vue.axios.get(`${resource}/${id}`).catch(parseError);
  },

  post(resource, params) {
    return Vue.axios
      .post(`${resource}`, params)
      .then(x => httpStatus(x))
      .catch(parseError);
  },

  put(resource, params) {
    return Vue.axios
      .put(`${resource}`, params)
      .then(x => httpStatus(x))
      .catch(parseError);
  },

  delete(resource, params) {
    return Vue.axios
      .delete(resource, params !== undefined ? { data: params } : null)
      .then(x => httpStatus(x))
      .catch(parseError);
  }
};

export default ApiService;

export const ClientsService = {
  query(params) {
    return ApiService.query("clients/", { params });
  },
  async get(id) {
    return ApiService.get("clients", id);
  },
  destroy(id) {
    return ApiService.delete("clients/" + id);
  },
  async add(entity) {
    await ApiService.post("clients/", {
      client: entity
    });
  },
  async save(entity) {
    return await ApiService.put("clients/" + entity.clientId, {
      client: entity
    });
  },
  async clone(entity) {
    return await ApiService.post("clients/clone", {
      client: entity
    });
  },
  addSecret(clientId, secret) {
    return ApiService.post("clients/secrets", {
      clientId: clientId,
      secret: secret
    });
  }
};

export const ApiResourcesService = {
  query(params) {
    return ApiService.query("apiresource/", { params });
  },
  async get(id) {
    return ApiService.get("apiresource", id);
  },
  destroy(id) {
    return ApiService.delete("apiresource/" + id);
  },
  async add(entity) {
    await ApiService.post("apiresource/", {
      apiResource: entity
    });
  },
  async save(entity) {
    return await ApiService.put("apiresource/" + entity.id, {
      apiResource: entity
    });
  },
  async saveScope(parentId, entity) {
    return await ApiService.put("apiresource/scopes/" + parentId, {
      scope: entity
    });
  },
  async addScope(parentId, entity) {
    return await ApiService.post("apiresource/scopes/" + parentId, {
      scope: entity
    });
  },
  async destroyScope(parentId, scopeId) {
    return await ApiService.delete(
      "apiresource/scopes/" + parentId + "/" + scopeId
    );
  }
};

export const IdentityResourcesService = {
  query(params) {
    return ApiService.query("identityresource/", { params });
  },
  async get(id) {
    return ApiService.get("identityresource", id);
  },
  destroy(id) {
    return ApiService.delete("identityresource/" + id);
  },
  async add(entity) {
    await ApiService.post("identityresource/", {
      identityResource: entity
    });
  },
  async save(entity) {
    return await ApiService.put("identityresource/" + entity.id, {
      identityResource: entity
    });
  }
};

export const UsersService = {
  query(params) {
    return ApiService.query("users/", { params });
  },

  async get(id) {
    return ApiService.get("users", id);
  },

  destroy(id) {
    return ApiService.delete("users/" + id);
  },

  async add(entity) {
    await ApiService.post("users/", {
      user: entity
    });
  },

  async save(entity) {
    await ApiService.put("users/" + entity.subject, {
      user: entity
    });
  },

  addRoles(subject, roles) {
    return ApiService.post("users/addroles", {
      subject: subject,
      roles: roles
    });
  },

  removeRoles(subject, roles) {
    return ApiService.delete("users/removeroles", {
      subject: subject,
      roles: roles
    });
  },

  addClaim(subject, claim) {
    return ApiService.post("users/addclaim", {
      subject: subject,
      claim: claim
    });
  },

  removeClaim(subject, claim) {
    return ApiService.delete("users/removeclaim", {
      subject: subject,
      claim: claim
    });
  },

  async changePassword(changePasswordDto) {
    return await ApiService.post("users/changepassword", {
      changePassword: changePasswordDto
    });
  },

  async generateConfirmMail(id) {
    return await ApiService.get("users/generateconfirmmail?userId=" + id);
  }
};

export const RolesService = {
  query(params) {
    return ApiService.query("roles/", { params });
  },

  async get(id) {
    return ApiService.get("roles", id);
  },

  destroy(id) {
    return ApiService.delete("roles/" + id);
  },
  async add(entity) {
    await ApiService.post("roles/", {
      role: entity
    });
  },
  async save(entity) {
    return await ApiService.put("roles/" + entity.id, {
      role: entity
    });
  }
};
